import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Chip, CircularProgress, Alert,
    Collapse, IconButton, FormControl, InputLabel, Select, MenuItem,
    Paper, TextField, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getUser } from '../../../auth/authService';
import './DivergenceDashboard.css';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const INTEGRATION_URL = process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL;
const API_BASE = `${INTEGRATION_URL}/divergence`;

async function apiFetch(path, options = {}) {
    const user = await getUser();
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const text = await res.text();
        let detail;
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    return res.json();
}

// ---------------------------------------------------------------------------
// Static maps
// ---------------------------------------------------------------------------

const SOURCE_STYLES = {
    daily_audit: { bg: '#e3f0ff', color: '#004df2', label: 'daily audit' },
    ci_dryrun:   { bg: '#f3e8ff', color: '#7b2d9a', label: 'ci dry-run' },
    operator:    { bg: '#f5f5f5', color: '#555',    label: 'operator' },
};

const CLASSIFICATION_CLASSES = {
    pattern_safe:       'mq-badge mq-badge-pattern-safe',
    rename_candidate:   'mq-badge mq-badge-rename-candidate',
    vendor_deprecated:  'mq-badge mq-badge-vendor-deprecated',
    manual_decision:    'mq-badge mq-badge-manual-decision',
};

function ClassificationBadge({ label, count, showZero = false }) {
    const cls = CLASSIFICATION_CLASSES[label] || 'mq-badge mq-badge-manual-decision';
    if (!count && !showZero) return null;
    return (
        <span className={cls}>
            {label.replace(/_/g, ' ')} {count}
        </span>
    );
}

function SourceBadge({ source }) {
    const s = SOURCE_STYLES[source] || { bg: '#f5f5f5', color: '#555', label: source };
    return (
        <Chip
            label={s.label}
            size="small"
            sx={{
                bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: 11,
                fontFamily: 'Verdana, sans-serif', height: 22,
            }}
        />
    );
}

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

function opDescription(op) {
    const kind = op.kind || '';
    const col = op.column_name || op.name || op.title || '(unknown)';
    const seg = op.segment_name || op._segment_path || '(this segment)';
    if (kind === 'datapoint.create') return `Add column "${col}"${op._segment_path ? ` to ${op._segment_path}` : ` to ${seg}`}`;
    if (kind === 'datapoint.update') return `Rename "${col}" → "${op.new_name}" in ${seg}`;
    if (kind === 'datapoint.delete') return `Remove column "${col}"${op._segment_path ? ` from ${op._segment_path}` : ` from ${seg}`}`;
    if (kind === 'segment.create') return `Create segment "${op.title || col}"${op.full_title ? ` (${op.full_title})` : ''}`;
    if (kind === 'segment.update') return `Update segment "${seg}"`;
    if (kind === 'segment.delete') return `Delete segment "${seg}"`;
    if (kind === 'segment.unregistered') return `⚠ Segment not registered: ${op._segment_path || ''} (${(op.test_columns || []).length} columns waiting)`;
    if (kind === 'segment.ambiguous') return `⚠ Segment ambiguous: ${op._segment_path || ''} (${(op.matching_segment_ids || []).length} matches)`;
    if (kind === 'segment.tag.add') return `Tag "${seg}" with "${op.tag}"`;
    if (kind === 'segment.tag.remove') return `Remove tag "${op.tag}" from "${seg}"`;
    if (kind === 'data_source.create') return `Create data source "${op.title || col}"${op._client_name ? ` for ${op._client_name}` : ''}`;
    if (kind === 'data_source.update') return `Update data source`;
    if (kind === 'data_source.delete') return `Delete data source`;
    if (kind === 'base_file.create') return `Register base file "${op.title || ''}"`;
    return JSON.stringify(op);
}

// ---------------------------------------------------------------------------
// Op row inside expanded migration
// ---------------------------------------------------------------------------

// Destructive op kinds need a 2-step confirm on Apply
const DESTRUCTIVE_KINDS = new Set([
    'datapoint.delete',
    'datapoint.update',  // rename replaces a datapoint
    'segment.delete',
    'data_source.delete',
    'partition.delete',
]);

function OpStatusChip({ status }) {
    if (!status || status === 'pending') return null;
    const styles = {
        applied:  { bg: '#e8f5e9', color: '#1b5e20', label: 'applied' },
        skipped:  { bg: '#f5f5f5', color: '#666',    label: 'skipped' },
        failed:   { bg: '#ffebee', color: '#c62828', label: 'failed' },
    };
    const s = styles[status] || styles.skipped;
    return (
        <Chip label={s.label} size="small"
            sx={{ bgcolor: s.bg, color: s.color, fontSize: 10, height: 18, fontWeight: 600 }} />
    );
}

// Detect ops that depend on cross-op aliases (cascade ops). The per-op apply
// endpoint can't resolve {$alias: ...} refs because alias_map only persists
// within a single migration-level apply. Force operators to use the migration
// Approve button for cascade migrations so all ops apply in order.
function opHasAlias(op) {
    if (op._alias) return true;  // op produces an alias other ops depend on
    for (const key of Object.keys(op || {})) {
        const v = op[key];
        if (v && typeof v === 'object' && '$alias' in v) return true;
    }
    return false;
}

function OpRow({ op, migrationId, onAction, onUnskip, busy }) {
    const [confirmingApply, setConfirmingApply] = useState(false);
    const status = op.op_status || 'pending';
    const hasAlias = opHasAlias(op);
    const actionable = status === 'pending' && !hasAlias;
    const undoable = status === 'skipped' || status === 'failed';
    const isDestructive = DESTRUCTIVE_KINDS.has(op.kind);

    async function handleApply() {
        if (isDestructive && !confirmingApply) {
            setConfirmingApply(true);
            return;
        }
        setConfirmingApply(false);
        await onAction(op.op_index, 'apply');
    }
    async function handleSkip() {
        await onAction(op.op_index, 'skip');
    }
    async function handleUndo() {
        await onUnskip(op.op_index);
    }

    return (
        <Box className={`mq-op-row ${op.pending_decision ? 'mq-op-row-pending' : ''}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {op.pending_decision && (
                    <Tooltip title="Pending operator decision">
                        <WarningAmberIcon sx={{ color: '#c62828', fontSize: 16 }} />
                    </Tooltip>
                )}
                <Chip
                    label={op.kind}
                    size="small"
                    sx={{
                        bgcolor: '#eef0ff', color: '#004df2',
                        fontFamily: 'monospace', fontSize: 11, fontWeight: 600, height: 20,
                    }}
                />
                <Typography className="d-font" sx={{ fontSize: 12, color: '#333', flexGrow: 1 }}>
                    {opDescription(op)}
                </Typography>
                {op.classification && (
                    <ClassificationBadge label={op.classification} count="" showZero />
                )}
                {op.persistence_days != null && (
                    <Typography className="d-font" sx={{ fontSize: 11, color: '#888' }}>
                        {op.persistence_days}d
                    </Typography>
                )}
                <OpStatusChip status={status} />

                {hasAlias && status === 'pending' && (
                    <Tooltip title="This op references another op in the same migration via an alias. Use the migration-level Approve button so all cascade ops apply in order.">
                        <Chip
                            label="cascade"
                            size="small"
                            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: 10, height: 18, fontWeight: 600 }}
                        />
                    </Tooltip>
                )}

                {/* Undo for skipped/failed ops */}
                {undoable && (
                    <Button
                        size="small"
                        variant="text"
                        disabled={busy}
                        onClick={handleUndo}
                        sx={{ fontSize: 10, py: 0.1, px: 0.75, minWidth: 0, height: 22, ml: 'auto', color: '#004df2' }}
                    >
                        {busy ? <CircularProgress size={10} /> : 'Undo'}
                    </Button>
                )}

                {/* Per-op actions for pending */}
                {actionable && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                        <Button
                            size="small"
                            variant={confirmingApply ? 'contained' : 'outlined'}
                            color={isDestructive ? 'error' : 'success'}
                            disabled={busy}
                            onClick={handleApply}
                            onBlur={() => setConfirmingApply(false)}
                            sx={{ fontSize: 10, py: 0.1, px: 0.75, minWidth: 0, height: 22 }}
                        >
                            {busy ? <CircularProgress size={10} />
                                : confirmingApply ? 'Confirm?'
                                : isDestructive ? 'Apply ⚠'
                                : 'Apply'}
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            disabled={busy}
                            onClick={handleSkip}
                            sx={{ fontSize: 10, py: 0.1, px: 0.75, minWidth: 0, height: 22, color: '#888' }}
                        >
                            Skip
                        </Button>
                    </Box>
                )}
            </Box>
            {op.classification_reason && (
                <Typography className="d-font" sx={{ fontSize: 11, color: '#666', pl: 3, mt: 0.25 }}>
                    {op.classification_reason}
                </Typography>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Migration row
// ---------------------------------------------------------------------------

function MigrationRowView({ migration, onApprove, onReject, onOpAction, onOpUnskip, busy, busyOpIndex }) {
    const [expanded, setExpanded] = useState(false);
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState('');

    const ops = Array.isArray(migration.parsed_ops) ? migration.parsed_ops : [];
    const opSummary = migration.op_summary || {};
    const cls = migration.classification_summary || {};

    function handleRejectSubmit() {
        onReject(migration.migration_id, reason);
        setRejectMode(false);
        setReason('');
    }

    return (
        <Box className="mq-migration">
            <Box className="mq-migration-head">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                    <SourceBadge source={migration.source} />
                    <Typography className="d-font" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#444' }}>
                        {migration.filename}
                    </Typography>
                    <Typography className="d-font" sx={{ fontSize: 11, color: '#888' }}>
                        {fmtDate(migration.created_at)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 1 }}>
                        {Object.entries(opSummary).map(([k, v]) => (
                            <Chip
                                key={k}
                                label={`${k} ×${v}`}
                                size="small"
                                sx={{
                                    bgcolor: '#f5f7ff', color: '#333',
                                    fontFamily: 'monospace', fontSize: 10, height: 20,
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <ClassificationBadge label="pattern_safe" count={cls.pattern_safe || 0} />
                        <ClassificationBadge label="rename_candidate" count={cls.rename_candidate || 0} />
                        <ClassificationBadge label="vendor_deprecated" count={cls.vendor_deprecated || 0} />
                        <ClassificationBadge label="manual_decision" count={cls.manual_decision || 0} />
                    </Box>
                    {migration.has_pending_decisions && (
                        <Tooltip title="Has ops pending operator decision">
                            <WarningAmberIcon sx={{ color: '#c62828', fontSize: 18 }} />
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                        disabled={!!busy}
                        onClick={() => onApprove(migration.migration_id)}
                        sx={{ bgcolor: '#004df2', textTransform: 'none', fontSize: 11, py: 0.25 }}
                    >
                        Approve
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                        disabled={!!busy}
                        onClick={() => setRejectMode(m => !m)}
                        sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
                    >
                        Reject
                    </Button>
                    {ops.length > 0 && (
                        <IconButton size="small" onClick={() => setExpanded(e => !e)}>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    )}
                    {busy && <CircularProgress size={14} />}
                </Box>
            </Box>

            <Collapse in={rejectMode} unmountOnExit>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, mb: 0.5, pl: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Reason (optional)"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{ className: 'd-font', sx: { fontSize: 12 } }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={handleRejectSubmit}
                        sx={{ textTransform: 'none', fontSize: 11 }}
                    >
                        Submit
                    </Button>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => { setRejectMode(false); setReason(''); }}
                        sx={{ textTransform: 'none', fontSize: 11 }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Collapse>

            <Collapse in={expanded} unmountOnExit>
                <Box className="mq-ops-list">
                    {ops.map((op, i) => (
                        <OpRow
                            key={i}
                            op={op}
                            migrationId={migration.migration_id}
                            onAction={(opIndex, action) => onOpAction(migration.migration_id, opIndex, action)}
                            onUnskip={(opIndex) => onOpUnskip(migration.migration_id, opIndex)}
                            busy={busyOpIndex === op.op_index}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Segment group (within a client)
// ---------------------------------------------------------------------------

function SegmentGroup({ segmentName, dataSource, migrations, onApprove, onReject, onOpAction, onOpUnskip, busyMap, busyOpKey }) {
    return (
        <Box className="mq-segment-group">
            <Box className="mq-segment-head">
                <Typography className="nexus-font" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {segmentName}
                </Typography>
                {dataSource && (
                    <Chip
                        label={dataSource}
                        size="small"
                        sx={{ bgcolor: '#eef0ff', color: '#004df2', fontSize: 10, height: 18 }}
                    />
                )}
                <Typography className="d-font" sx={{ fontSize: 11, color: '#888' }}>
                    {migrations.length} migration{migrations.length === 1 ? '' : 's'}
                </Typography>
            </Box>
            <Box>
                {migrations.map(m => (
                    <MigrationRowView
                        key={m.migration_id}
                        migration={m}
                        onApprove={onApprove}
                        onReject={onReject}
                        onOpAction={onOpAction}
                        onOpUnskip={onOpUnskip}
                        busy={busyMap[m.migration_id]}
                        busyOpIndex={busyOpKey && busyOpKey.startsWith(`${m.migration_id}:`)
                            ? parseInt(busyOpKey.split(':')[1], 10) : null}
                    />
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Client group (collapsible)
// ---------------------------------------------------------------------------

function ClientGroup({ clientName, migrations, onApprove, onReject, onOpAction, onOpUnskip, onBulkApprovePatternSafe, busyMap, busyOpKey, bulkBusy }) {
    const [open, setOpen] = useState(true);

    // Aggregate classification summary across migrations
    const totalCls = migrations.reduce((acc, m) => {
        const c = m.classification_summary || {};
        for (const k of Object.keys(c)) acc[k] = (acc[k] || 0) + (c[k] || 0);
        return acc;
    }, {});

    // Bulk-eligible: pattern_safe>0 AND no pending decisions
    const bulkEligible = migrations.filter(
        m => (m.classification_summary?.pattern_safe || 0) > 0 && !m.has_pending_decisions
    );
    const showBulk = bulkEligible.length > 0;

    // Group by segment
    const segmentGroups = useMemo(() => {
        const map = new Map();
        for (const m of migrations) {
            const key = m.segment_id || m.segment_name || '_';
            if (!map.has(key)) {
                map.set(key, {
                    segmentName: m.segment_name || '(unknown segment)',
                    dataSource: m.data_source,
                    migrations: [],
                });
            }
            map.get(key).migrations.push(m);
        }
        return Array.from(map.values()).sort((a, b) => a.segmentName.localeCompare(b.segmentName));
    }, [migrations]);

    return (
        <Paper elevation={0} className="mq-client-card">
            <Box
                className="mq-client-head"
                onClick={() => setOpen(o => !o)}
            >
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
                    {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography className="nexus-font" sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
                    {clientName}
                </Typography>
                <Typography className="d-font" sx={{ fontSize: 12, color: '#666' }}>
                    {migrations.length} pending
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <ClassificationBadge label="pattern_safe" count={totalCls.pattern_safe || 0} />
                    <ClassificationBadge label="rename_candidate" count={totalCls.rename_candidate || 0} />
                    <ClassificationBadge label="vendor_deprecated" count={totalCls.vendor_deprecated || 0} />
                    <ClassificationBadge label="manual_decision" count={totalCls.manual_decision || 0} />
                </Box>
                {showBulk && (
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={bulkBusy}
                        onClick={(e) => {
                            e.stopPropagation();
                            onBulkApprovePatternSafe(bulkEligible);
                        }}
                        sx={{ textTransform: 'none', fontSize: 11, py: 0.25, ml: 1 }}
                    >
                        {bulkBusy ? <CircularProgress size={12} /> : `Approve all pure pattern-safe (${bulkEligible.length})`}
                    </Button>
                )}
            </Box>
            <Collapse in={open} unmountOnExit>
                <Box className="mq-client-body">
                    {segmentGroups.map(g => (
                        <SegmentGroup
                            key={g.segmentName}
                            segmentName={g.segmentName}
                            dataSource={g.dataSource}
                            migrations={g.migrations}
                            onApprove={onApprove}
                            onReject={onReject}
                            onOpAction={onOpAction}
                            onOpUnskip={onOpUnskip}
                            busyMap={busyMap}
                            busyOpKey={busyOpKey}
                        />
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MigrationQueue() {
    const [migrations, setMigrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [busyMap, setBusyMap] = useState({});
    const [bulkBusyClient, setBulkBusyClient] = useState(null);

    // Filters
    const [filterSource, setFilterSource] = useState('all');
    const [filterClassification, setFilterClassification] = useState('all');
    const [filterClient, setFilterClient] = useState('all');

    function showToast(msg, type = 'success') {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    }

    const fetchMigrations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (filterClient !== 'all') params.set('client_id', filterClient);
            if (filterSource !== 'all') params.set('source', filterSource);
            if (filterClassification !== 'all') params.set('classification', filterClassification);
            const qs = params.toString();
            const data = await apiFetch(`/migrations/pending${qs ? `?${qs}` : ''}`);
            setMigrations(data.pending || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [filterClient, filterSource, filterClassification]);

    useEffect(() => {
        fetchMigrations();
    }, [fetchMigrations]);

    // Build client dropdown options from current data
    const clientOptions = useMemo(() => {
        const seen = new Map();
        for (const m of migrations) {
            if (m.client_id && !seen.has(m.client_id)) {
                seen.set(m.client_id, m.client_name || m.client_id);
            }
        }
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [migrations]);

    // Group migrations by client
    const clientGroups = useMemo(() => {
        const map = new Map();
        for (const m of migrations) {
            const key = m.client_id || '_';
            if (!map.has(key)) {
                map.set(key, { clientId: key, clientName: m.client_name || '(unknown)', migrations: [] });
            }
            map.get(key).migrations.push(m);
        }
        return Array.from(map.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
    }, [migrations]);

    const handleApprove = useCallback(async (id) => {
        setBusyMap(b => ({ ...b, [id]: true }));
        try {
            await apiFetch(`/migrations/${id}/apply`, { method: 'POST' });
            showToast('Migration approved.');
            await fetchMigrations();
        } catch (e) {
            showToast(e.message, 'error');
        } finally {
            setBusyMap(b => ({ ...b, [id]: false }));
        }
    }, [fetchMigrations]);

    const [busyOpKey, setBusyOpKey] = useState(null);  // `${migId}:${opIdx}`

    const handleOpAction = useCallback(async (migId, opIndex, action) => {
        const key = `${migId}:${opIndex}`;
        if (action !== 'apply' && action !== 'skip') {
            console.error('[op_action] BLOCKED — invalid action:', action);
            showToast(`invalid action: ${action}`, 'error');
            return;
        }
        console.log(`[op_action] sending migration=${migId} op=${opIndex} action=${action}`);
        setBusyOpKey(key);
        try {
            const res = await apiFetch(`/migrations/${migId}/ops/${opIndex}/action`, {
                method: 'POST',
                body: JSON.stringify({ action }),
            });
            const newStatus = res.op_status || (action === 'apply' ? (res.success ? 'applied' : 'failed') : 'skipped');
            // Optimistic local update — only drop migration when ALL ops are applied/skipped (NOT failed)
            setMigrations(prev => prev.map(m => {
                if (m.migration_id !== migId) return m;
                const newOps = (m.parsed_ops || []).map(op =>
                    op.op_index === opIndex ? { ...op, op_status: newStatus, op_error: res.error } : op
                );
                const allClosed = newOps.length > 0 && newOps.every(op =>
                    op.op_status === 'applied' || op.op_status === 'skipped'
                );
                return allClosed ? null : { ...m, parsed_ops: newOps };
            }).filter(Boolean));
            if (newStatus === 'failed') {
                showToast(`Apply failed: ${res.error || 'unknown error'}`, 'error');
            } else {
                showToast(`Op ${newStatus}.`);
            }
        } catch (e) {
            showToast(e.message, 'error');
        } finally {
            setBusyOpKey(null);
        }
    }, []);

    const handleUnskip = useCallback(async (migId, opIndex) => {
        const key = `${migId}:${opIndex}`;
        setBusyOpKey(key);
        try {
            await apiFetch(`/migrations/${migId}/ops/${opIndex}/unskip`, { method: 'POST' });
            setMigrations(prev => prev.map(m => {
                if (m.migration_id !== migId) return m;
                return {
                    ...m,
                    parsed_ops: (m.parsed_ops || []).map(op =>
                        op.op_index === opIndex ? { ...op, op_status: 'pending', op_error: null } : op
                    ),
                };
            }));
            showToast('Reverted to pending.');
        } catch (e) {
            showToast(e.message, 'error');
        } finally {
            setBusyOpKey(null);
        }
    }, []);

    const handleReject = useCallback(async (id, reason) => {
        setBusyMap(b => ({ ...b, [id]: true }));
        try {
            await apiFetch(`/migrations/${id}/reject`, {
                method: 'POST',
                body: JSON.stringify({ reason: reason || '' }),
            });
            showToast('Migration rejected.');
            await fetchMigrations();
        } catch (e) {
            showToast(e.message, 'error');
        } finally {
            setBusyMap(b => ({ ...b, [id]: false }));
        }
    }, [fetchMigrations]);

    const handleBulkApprovePatternSafe = useCallback(async (eligible) => {
        if (!eligible.length) return;
        const clientId = eligible[0].client_id;
        setBulkBusyClient(clientId);
        let ok = 0, fail = 0;
        for (const m of eligible) {
            try {
                await apiFetch(`/migrations/${m.migration_id}/apply`, { method: 'POST' });
                ok += 1;
            } catch {
                fail += 1;
            }
        }
        setBulkBusyClient(null);
        showToast(`Bulk approved ${ok} pattern-safe migration${ok === 1 ? '' : 's'}${fail ? ` (${fail} failed)` : ''}.`,
            fail ? 'error' : 'success');
        await fetchMigrations();
    }, [fetchMigrations]);

    return (
        <Box className="mq-container">
            {/* Header */}
            <Box className="dd-header-row">
                <Box>
                    <Typography className="nexus-font lg">Migration Queue</Typography>
                    <Typography className="d-font md" sx={{ color: '#666', mt: 0.5 }}>
                        Unified queue of pending migrations from CI dry-runs, daily audits, and operator submissions.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                    disabled={loading}
                    onClick={fetchMigrations}
                    className="d-font"
                >
                    {loading ? 'Loading…' : 'Refresh'}
                </Button>
            </Box>

            {/* Filter chips */}
            <Box className="mq-filter-row">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel className="d-font">Source</InputLabel>
                    <Select
                        value={filterSource}
                        label="Source"
                        onChange={e => setFilterSource(e.target.value)}
                        className="d-font"
                    >
                        <MenuItem value="all" className="d-font">All sources</MenuItem>
                        <MenuItem value="daily_audit" className="d-font">Daily audit</MenuItem>
                        <MenuItem value="ci_dryrun" className="d-font">CI dry-run</MenuItem>
                        <MenuItem value="operator" className="d-font">Operator</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel className="d-font">Classification</InputLabel>
                    <Select
                        value={filterClassification}
                        label="Classification"
                        onChange={e => setFilterClassification(e.target.value)}
                        className="d-font"
                    >
                        <MenuItem value="all" className="d-font">All classifications</MenuItem>
                        <MenuItem value="pattern_safe" className="d-font">Pattern safe</MenuItem>
                        <MenuItem value="rename_candidate" className="d-font">Rename candidate</MenuItem>
                        <MenuItem value="vendor_deprecated" className="d-font">Vendor deprecated</MenuItem>
                        <MenuItem value="manual_decision" className="d-font">Manual decision</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel className="d-font">Client</InputLabel>
                    <Select
                        value={filterClient}
                        label="Client"
                        onChange={e => setFilterClient(e.target.value)}
                        className="d-font"
                    >
                        <MenuItem value="all" className="d-font">All clients</MenuItem>
                        {clientOptions.map(c => (
                            <MenuItem key={c.id} value={c.id} className="d-font">{c.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {toast && (
                <Alert
                    severity={toast.type === 'error' ? 'error' : 'success'}
                    sx={{ mb: 2 }}
                    onClose={() => setToast(null)}
                >
                    {toast.msg}
                </Alert>
            )}

            {loading && migrations.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : clientGroups.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography className="d-font" sx={{ color: '#888' }}>
                        No pending migrations match the current filters.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {clientGroups.map(g => (
                        <ClientGroup
                            key={g.clientId}
                            clientName={g.clientName}
                            migrations={g.migrations}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onOpAction={handleOpAction}
                            onOpUnskip={handleUnskip}
                            onBulkApprovePatternSafe={handleBulkApprovePatternSafe}
                            busyMap={busyMap}
                            busyOpKey={busyOpKey}
                            bulkBusy={bulkBusyClient === g.clientId}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}
